import { emailValidator, nameValidator, phoneValidatorRequired } from "@asd/customer-portal-ui";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import PersonIcon from "@mui/icons-material/Person";
import { Alert, AlertTitle, InputAdornment, Link, Stack, TextField, Typography } from "@mui/material";
import { useMask } from "@react-input/mask";
import { Controller, useFormContext } from "react-hook-form";

import { useKeycloak } from "~/components/authorization";
import { PROVIDER_REGISTRATION_TITLE_PROPS } from "~/utils/providerRegistrationHelpers";
import BooleanRadioGroup from "./BooleanRadioGroup";

const ContactInfo = () => {
  const { userInfo } = useKeycloak();
  const { control, register } = useFormContext();

  const phoneInputRef = useMask({
    mask: "(___) ___-____",
    replacement: { _: /\d/ },
    modify: ({ value, data, selectionStart, selectionEnd }) => {
      const newValue = value.slice(0, selectionStart) + (data ?? "") + value.slice(selectionEnd);
      return { showMask: !!newValue?.length && /\d/.test(newValue) };
    },
  });

  return (
    <Stack direction="column" spacing={4}>
      <Stack direction="column" spacing={2}>
        <Typography align="left" {...PROVIDER_REGISTRATION_TITLE_PROPS}>
          Enter your name as you would like it to appear on the provider directory (e.g., Mary Smith, Example Company<sup>TM</sup>):
        </Typography>
        <Controller
          control={control}
          defaultValue=""
          name="contact.name"
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...register("contact.name", nameValidator)}
              {...field}
              autoComplete="name"
              error={!!error}
              helperText={error ? error.message : null}
              label="Name"
              onChange={(e) => {
                field.onChange(e);
              }}
              type="text"
              required
            />
          )}
        />
      </Stack>
      <Stack direction="column" spacing={2}>
        <Typography align="left" {...PROVIDER_REGISTRATION_TITLE_PROPS}>
          What is the best phone number to call you at?
        </Typography>
        <Controller
          control={control}
          defaultValue=""
          name="contact.phoneNumber"
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...register("contact.phoneNumber", phoneValidatorRequired)}
              {...field}
              autoComplete="tel"
              error={!!error}
              helperText={error ? error.message : null}
              inputRef={phoneInputRef}
              label="Phone Number"
              onChange={(e) => {
                field.onChange(e);
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocalPhoneIcon />
                    </InputAdornment>
                  ),
                },
              }}
              type="text"
              required
            />
          )}
        />
      </Stack>
      <Stack direction="column" spacing={2}>
        <Typography align="left" {...PROVIDER_REGISTRATION_TITLE_PROPS}>
          Can you receive text messages at the phone number listed above?
        </Typography>
        <Alert color="alertinfo" severity="info" sx={{ mb: 2 }}>
          <AlertTitle>Disclaimer</AlertTitle>
          <Typography component="p" variant="body2">
            By selecting &quot;Yes&quot;, you agree to receive account notifications. Standard data and message rates may apply. Message
            frequency may vary. More details can be found in our{" "}
            <Typography
              color="text.primary"
              component={Link}
              href={`${window.location.origin}/privacy-policy.htm`}
              rel="noopener noreferrer"
              target="_blank"
              variant="body2"
            >
              Privacy Policy
            </Typography>{" "}
            and{" "}
            <Typography
              color="text.primary"
              component={Link}
              href={`${window.location.origin}/terms-and-conditions.htm`}
              rel="noopener noreferrer"
              target="_blank"
              variant="body2"
            >
              Terms and Conditions
            </Typography>
          </Typography>
        </Alert>
        <BooleanRadioGroup control={control} name="contact.phoneTextSupport" />
      </Stack>
      <Stack direction="column" spacing={2}>
        <Typography align="left" {...PROVIDER_REGISTRATION_TITLE_PROPS}>
          Please verify that the email address below is best to contact you for work inquiries:
        </Typography>
        <Controller
          control={control}
          defaultValue={userInfo?.email || ""}
          name="contact.workEmailAddress"
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...register("contact.workEmailAddress", emailValidator)}
              {...field}
              autoComplete="email"
              error={!!error}
              helperText={error ? error.message : null}
              label="Email Address"
              onChange={(e) => {
                field.onChange(e);
              }}
              placeholder={userInfo?.email || ""}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                },
              }}
              type="email"
              required
            />
          )}
        />
      </Stack>
    </Stack>
  );
};

export default ContactInfo;
